import React from 'react';
import styled from 'styled-components';
import { OverlayTrigger } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { sub } from 'date-fns';

import type { IRequirementValue } from '@/components/agent/gig-compliance/types';
import type { IContractRequirementItem, IScheduleSummary } from '@/store/types';

import { RequirementType } from '@/store/enums';
import { RequirementText } from '@/components/common';
import { RowCell } from '@/components/agent/gig-compliance/ComplianceTable.styles';
import { classList, formatDate } from '@/helpers';
import { AM_PM_TIME_SPACE_UPPERCASE_FORMAT, WEEK_DAY_MONTH_DAY, Z_INDEX_BOTTOM_DRAWER } from '@/config/variables';
import { getDateFromBlock } from '@/helpers/block';
import { useTimezone } from '@/hooks';

const RequirementCell = ({
  type,
  value,
  weekId,
  index = 0,
  requirement,
  contractSchedules,
  isRowPublished,
}: {
  type: RequirementType;
  value: IRequirementValue | undefined;
  weekId: number;
  index?: number;
  requirement: IContractRequirementItem | undefined;
  contractSchedules: IScheduleSummary[];
  isRowPublished: boolean;
}): React.ReactElement => {
  const { t } = useTranslation();
  const { timezoneName } = useTimezone();

  const cellContent = (
    <RowReqCell className={classList(value?.isWarningGig && 'warning')}>
      {!!value?.networkValue && (
        <>
          <NetworkText className={classList((value?.isWarningNetwork || value?.isWarningGig) && 'warning')}>
            {value?.networkValue}
          </NetworkText>
          /
        </>
      )}
      {value?.gigValue}
    </RowReqCell>
  );

  const getComplianceDeadline = (type: 'network' | 'gig', blocks: number) => {
    if (!blocks) return '';
    const complianceDate = sub(getDateFromBlock(blocks, timezoneName), {
      minutes: 1,
    });
    const time = formatDate(complianceDate, AM_PM_TIME_SPACE_UPPERCASE_FORMAT);
    const date = formatDate(complianceDate, WEEK_DAY_MONTH_DAY);
    return t(`gigCompliance.complianceTable.complianceDeadline.${type}`, { time, date });
  };

  if (!isRowPublished) {
    return <RowReqCell className="unpublished">-</RowReqCell>;
  }

  if (!requirement) return <>{cellContent}</>;

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement="auto-end"
      show={value?.gigValue === '-' ? false : undefined}
      overlay={
        <TooltipContainer id={`tooltip-${type}-${weekId}-${index}`}>
          {requirement && <RequirementText requirement={requirement} contractSchedules={contractSchedules} />}
          <div className="mt-3">
            {t`gigCompliance.complianceTable.networkCompliance`}
            <TooltipHours isWarning={value?.isWarningNetwork}>
              {value?.networkValue}/{value?.blocksValue}
            </TooltipHours>
          </div>
          {value?.networkDeadline && <div>{getComplianceDeadline('network', value?.networkDeadline)}</div>}
          <div className="mt-3">
            {t`gigCompliance.complianceTable.gigCompliance`}
            <TooltipHours isWarning={value?.isWarningGig}>
              {value?.gigValue}/{value?.blocksValue}
            </TooltipHours>
          </div>
          {value?.gigDeadline && <div>{getComplianceDeadline('gig', value.gigDeadline)}</div>}
        </TooltipContainer>
      }
    >
      {cellContent}
    </OverlayTrigger>
  );
};

export { RequirementCell };

const TooltipContainer = styled.div`
  background-color: #243342;
  max-width: 350px;
  padding: 0.25rem 0.5rem;
  gap: 10px;
  border-radius: 0.25rem;
  z-index: ${Z_INDEX_BOTTOM_DRAWER};

  & > div {
    color: white;
    font-size: 13px;
    line-height: 15px;
  }

  .day {
    width: 25% !important;
  }
`;

const TooltipHours = styled.span<{ isWarning?: boolean }>`
  color: ${({ isWarning }) => (!!isWarning ? '#dc3545' : '#10b981')};
`;

const NetworkText = styled.div`
  color: #047857;

  &.warning {
    color: #b91c1c;
  }
`;

const RowReqCell = styled(RowCell)`
  flex-grow: 1;
  min-width: 65px;
`;
